import React, { useEffect, useRef, useState } from 'react';
import AccountPreferences from '../AccountPreferences';

// Clickable avatar in the topbar. Click opens a dropdown anchored to the
// avatar with the user's name + email + sign-out action. Outside click,
// Escape, or selecting an item closes it. Replaces the standalone
// "Sign out" pill that used to sit next to the avatar.
export default function ProfileMenu({
  user,
  onSignOut,
  preferencesOpen,
  onPreferencesOpenChange,
  onProfileSettingsSaved,
}) {
  const [open, setOpen] = useState(false);
  const [localPrefsOpen, setLocalPrefsOpen] = useState(false);
  const prefsControlled = typeof onPreferencesOpenChange === 'function';
  const prefsOpen = prefsControlled ? Boolean(preferencesOpen) : localPrefsOpen;
  const setPrefsOpen = prefsControlled ? onPreferencesOpenChange : setLocalPrefsOpen;
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} style={wrapStyle}>
      <button
        type="button"
        style={avatarBtnStyle}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open profile menu"
      >
        {initials(user)}
      </button>
      {open && (
        <div role="menu" style={menuStyle}>
          <div style={menuHeadStyle}>
            <div style={menuNameStyle}>{user?.name || user?.email || 'Signed in'}</div>
            {user?.email && user?.name && <div style={menuEmailStyle}>{user.email}</div>}
          </div>
          <div style={dividerStyle} aria-hidden="true" />
          <button
            type="button"
            role="menuitem"
            style={menuItemStyle}
            onClick={() => {
              setPrefsOpen(!prefsOpen);
              if (!prefsOpen) setOpen(true);
            }}
          >
            Account preferences
          </button>
          {prefsOpen && (
            <AccountPreferences
              onClose={() => setPrefsOpen(false)}
              onSaved={onProfileSettingsSaved}
            />
          )}
          <div style={dividerStyle} aria-hidden="true" />
          {onSignOut && (
            <button
              type="button"
              role="menuitem"
              style={menuItemStyle}
              onClick={() => { setOpen(false); onSignOut(); }}
            >
              Sign out
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function initials(user) {
  const source = (user?.name || user?.email || '?').trim();
  const parts = source.split(/\s+|@/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

const wrapStyle = { position: 'relative' };
const avatarBtnStyle = {
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  background: 'var(--warm)',
  color: 'var(--paper)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--font)',
};

const menuStyle = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  minWidth: '220px',
  maxWidth: '380px',
  background: 'var(--paper)',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  boxShadow: 'var(--shadow-sm)',
  padding: '6px',
  zIndex: 100,
  fontFamily: 'var(--font)',
};

const menuHeadStyle = { padding: '8px 10px 6px' };
const menuNameStyle = { fontSize: '13px', fontWeight: 600, color: 'var(--ink)' };
const menuEmailStyle = { fontSize: '12px', color: 'var(--muted)', marginTop: '2px' };

const dividerStyle = { height: '1px', background: 'var(--line)', margin: '4px 0' };

const menuItemStyle = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '8px 10px',
  borderRadius: '4px',
  border: 'none',
  background: 'transparent',
  color: 'var(--ink2)',
  fontSize: '13px',
  cursor: 'pointer',
  fontFamily: 'var(--font)',
};
